const COOKIE_KEY = 'cookie_consent';
const TTL_DAYS = 180;

type ConsentValue = 'accepted' | 'declined';

const setCookie = (key: string, value: ConsentValue, days: number): void => {
    document.cookie = `${key}=${value}; max-age=${days * 86400}; path=/; SameSite=Lax`;
};

const getCookie = (key: string): string | undefined =>
    document.cookie
        .split('; ')
        .find(c => c.startsWith(`${key}=`))
        ?.split('=')[1];

declare global {
    interface Window {
        gtagId?: string | null;
        recaptchaSiteKey?: string | null;
        hasTurnstile?: boolean;
        cf7ScriptUrl?: string | null;
    }
}

const dismissBanner = (banner: HTMLElement, state: ConsentValue): void => {
    banner.setAttribute('data-state', state);
    banner.classList.remove('is-active');
};

const onConsent = (): void => {
    const gtagId = window.gtagId;

    if (gtagId) {
        import('@components/analytics').then(({ default: initAnalytics }) => initAnalytics(gtagId));
    }

    if (document.querySelector('.map-zone')) {
        import('@components/map').then(({ default: initMap }) => initMap());
    }

    const recaptchaSiteKey = window.recaptchaSiteKey;

    if (window.hasTurnstile) {
        const cf7Script = document.createElement('script');
        cf7Script.src = window.cf7ScriptUrl ?? '';
        document.head.appendChild(cf7Script);

    } else if (recaptchaSiteKey) {
        // CF7 script
        const cf7Script = document.createElement('script');
        cf7Script.src = window.cf7ScriptUrl ?? '';
        document.head.appendChild(cf7Script);

        // reCAPTCHA v3
        const recaptchaScript = document.createElement('script');
        recaptchaScript.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
        recaptchaScript.async = true;
        recaptchaScript.onerror = () => console.warn('reCAPTCHA blocked (tracking protection)');
        document.head.appendChild(recaptchaScript);
    }

    document.getElementById('form-cookie-accept')?.closest('.cookie-consent')?.remove();

    document.querySelector('.map-zone')?.classList.add('is-consented');
    document.querySelector('.contact-form')?.classList.add('is-consented');
};

const accept = (): void => {
    setCookie(COOKIE_KEY, 'accepted', TTL_DAYS);
    const banner = document.getElementById('cookie-banner');
    if (banner) dismissBanner(banner, 'accepted');
    onConsent();
};

const setupBanner = (): void => {
    const banner = document.getElementById('cookie-banner');
    if (!banner) return;

    const acceptBtn = banner.querySelector<HTMLElement>('#cookie-accept');
    const declineBtn = banner.querySelector<HTMLElement>('#cookie-decline');
    if (!acceptBtn || !declineBtn) return;

    const isIndex = document.body.getAttribute('data-view') === 'index';
    if (isIndex) {
        setTimeout(() => banner.classList.add('is-active'), 2500);
    } else {
        banner.classList.add('is-active');
    }

    acceptBtn.addEventListener('click', accept);
    declineBtn.addEventListener('click', () => {
        setCookie(COOKIE_KEY, 'declined', TTL_DAYS);
        dismissBanner(banner, 'declined');
    });
};

const setupConsentButtons = (): void => {
    document.getElementById('map-cookie-accept')?.addEventListener('click', accept);
    document.getElementById('form-cookie-accept')?.addEventListener('click', accept);
};

export default function setupConsent(): void {
    const consent = getCookie(COOKIE_KEY);

    if (consent === 'accepted') {
        onConsent();
    } else if (!consent) {
        setupBanner();
        setupConsentButtons();
    }
}
